<template>
	<div ref="sectionRef" class="privilege" :class="{ 'privilege--en': locale === 'en' }">
		<div class="privilege__inner pc-center-1200">
			<div class="privilege__title">{{ t("index.privilege.title") }}</div>

			<div class="privilege__body">
				<div class="privilege__left">
					<div
						v-for="(item, index) in featureList"
						:key="item.id"
						class="privilege__feature"
						role="button"
						tabindex="0"
						@click="handleBenefitClick(item, index)"
						@keydown.enter="handleBenefitClick(item, index)"
					>
						<div class="privilege__feature-icon">
							<img :src="item.icon" :alt="item.title1" />
						</div>

						<p class="privilege__feature-text">
							<span>{{ item.title1 }}</span>

							<span>{{ item.title2 }}</span>
						</p>
					</div>
				</div>

				<div class="privilege__right">
					<h3 class="privilege__cta-title">{{ t("index.privilege.ctaTitle") }}</h3>

					<p class="privilege__cta-desc">{{ t("index.privilege.ctaDesc") }}</p>

					<div class="privilege__downloads">
						<button
							type="button"
							class="privilege__download-btn"
							aria-label="Download on Google Play"
							@click="toAppStore('android')"
						>
							<img :src="frame" alt="" aria-hidden="true" />

							<span class="privilege__download-text">
								<span class="privilege__download-label">{{ t("index.privilege.downloadOn") }}</span>

								<span class="privilege__download-name">Google Play</span>
							</span>
						</button>

						<button
							type="button"
							class="privilege__download-btn"
							aria-label="Download on App Store"
							@click="toAppStore('ios')"
						>
							<img :src="apple" alt="" aria-hidden="true" />

							<span class="privilege__download-text">
								<span class="privilege__download-label">{{ t("index.privilege.downloadOn") }}</span>

								<span class="privilege__download-name">App Store</span>
							</span>
						</button>

						<a
							:href="apkLink || 'javascript:void(0)'"
							class="privilege__download-btn"
							:download="apkLink ? true : undefined"
							aria-label="Download Android APK"
							@click="handleApkDownload"
						>
							<img :src="android" alt="" aria-hidden="true" />

							<span class="privilege__download-text">
								<span class="privilege__download-label">{{ t("index.privilege.apkLabel") }}</span>

								<span class="privilege__download-name">Android Apk</span>
							</span>
						</a>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import { computed, ref } from "vue";

import { useI18n } from "~/composables/useI18n";
import {
	useHomepageTracking,
	useSectionView,
} from "@variants/index/composables/useHomepageTracking";

import { downloadApp } from "~/utils/common";

import { getDownloadApk } from "~/api/api";

import jd from "../../../images/pc/jd@2x.png";

import mx from "../../../images/pc/mx@2x.png";

import qft from "../../../images/pc/qft@2x.png";

import video from "../../../images/pc/video@2x.png";

import vip from "../../../images/pc/vip@2x.png";

import apple from "../../../images/pc/apple.png";

import frame from "../../../images/pc/frame.png";

import android from "../../../images/pc/android.png";

const { t, locale } = useI18n();
const { track } = useHomepageTracking("pc");
const sectionRef = ref(null);

useSectionView(sectionRef, {
	track,
	sectionId: "app_benefits",
});

const { data: apkLink } = await useLazyAsyncData("home-privilege-download-apk", async () => {
	const res = await getDownloadApk();

	return res?.data?.link || "";
});

const featureList = computed(() => [
	{
		id: "bean",

		icon: jd,

		title1: t("index.privilege.features.bean.title1"),

		title2: t("index.privilege.features.bean.title2"),
	},

	{
		id: "vip",

		icon: vip,

		title1: t("index.privilege.features.vip.title1"),

		title2: t("index.privilege.features.vip.title2"),
	},

	{
		id: "pray",

		icon: qft,

		title1: t("index.privilege.features.pray.title1"),

		title2: t("index.privilege.features.pray.title2"),
	},

	{
		id: "live",

		icon: video,

		title1: t("index.privilege.features.live.title1"),

		title2: t("index.privilege.features.live.title2"),
	},

	{
		id: "meditation",

		icon: mx,

		title1: t("index.privilege.features.meditation.title1"),

		title2: t("index.privilege.features.meditation.title2"),
	},
]);

function handleBenefitClick(item, index) {
	track("app_benefit_click", {
		benefit_id: item.id,
		benefit_name: `${item.title1}${item.title2}`,
		position: index + 1,
	});
}

const STORE_TYPE_MAP = {
	android: "google_play",
	ios: "app_store",
};

function toAppStore(type) {
	track("app_store_click", {
		section_id: "app_benefits",
		store_type: STORE_TYPE_MAP[type] || type,
	});
	downloadApp(type);
}

function handleApkDownload() {
	track("app_store_click", {
		section_id: "app_benefits",
		store_type: "android_apk",
	});
	if (!apkLink.value) return;
}
</script>

<style lang="scss" scoped>
.privilege {
	background: linear-gradient(to bottom, #fffaef, #ffffff, #fffcef);
	margin: 80px 0 100px;

	&__inner {
		padding: 40px 0 50px;
	}

	&__title {
		margin-bottom: 40px;

		font-size: 32px;

		line-height: 44px;

		color: #333333;

		text-align: center;

		font-weight: bold;
	}

	&__body {
		display: flex;

		align-items: center;

		justify-content: space-between;

		gap: 40px;
	}

	&__left {
		flex: 1;
		display: flex;

		justify-content: space-between;

		min-width: 0;
	}

	&__feature {
		flex: 1;

		display: flex;

		flex-direction: column;

		align-items: center;

		min-width: 0;
		cursor: pointer;
	}

	&__feature-icon {
		width: 70px;

		height: 70px;

		display: flex;

		align-items: center;

		justify-content: center;

		img {
			width: 100%;

			height: 100%;

			object-fit: contain;
		}
	}

	&__feature-text {
		display: flex;

		flex-direction: column;

		align-items: center;

		gap: 4px;

		margin: 12px 0 0;

		font-size: 16px;

		line-height: 22px;

		color: #333333;

		text-align: center;
	}

	&__right {
		flex: 1.1;
		flex-shrink: 0;

		width: 420px;
	}

	&__cta-title {
		margin: 0;

		font-size: 24px;

		line-height: 34px;

		color: #333333;

		font-weight: bold;
	}

	&__cta-desc {
		margin: 8px 0 20px;

		font-size: 18px;

		line-height: 24px;

		color: rgba(51, 51, 51, 0.45);
	}

	&__downloads {
		display: flex;

		gap: 12px;
	}

	&__download-btn {
		display: flex;

		align-items: center;
		justify-content: center;

		gap: 12px;

		width: 100%;

		height: 56px;

		padding: 0 16px;

		border: 0;

		border-radius: 8px;

		background: #333333;

		color: #ffffff;

		text-decoration: none;

		cursor: pointer;

		img {
			width: 28px;

			height: 28px;

			flex-shrink: 0;

			object-fit: contain;
		}
	}

	&__download-text {
		display: flex;

		flex-direction: column;

		align-items: flex-start;

		gap: 2px;
	}

	&__download-label {
		font-size: 12px;

		line-height: 16px;

		color: #ffffff;
		font-weight: bold;
	}

	&__download-name {
		font-size: 18px;

		line-height: 22px;

		font-weight: bold;
	}

	&--en &__feature-text {
		font-size: 13px;
		line-height: 18px;
	}
}
</style>
